import { useEffect, useState } from 'react'
import { getTransactionRequest, getTxHashByTransactionRequestId, TransactionRequestStatus } from '../../api'
import { TransactionStatus } from '../../components/TxDetail'

export type TransactionResult = {
  hash: string
  status: TransactionStatus
  chain_id: number
} | null

/**
 * Hook for polling the status of a transaction request
 * @param transactionRequestId - ID of the transaction request
 * @returns An object with hash, status and chain_id if the transaction has completed, or null if it's in process
 */
const useTransactionRequestStatusPolling = (transactionRequestId: number): [TransactionResult, boolean] => {
  const [isTransactionRequestPolling, togglePolling] = useState<boolean>(false)
  const [transactionResult, setTransactionResult] = useState<TransactionResult>(null)

  useEffect(() => {
    if (transactionRequestId) {
      togglePolling(true)
      setTransactionResult(null)
      runPolling()
    }
  }, [transactionRequestId]) /* eslint-disable-line react-hooks/exhaustive-deps */

  const runPolling = () => {
    setTimeout(async () => {
      try {
        const response = await getTransactionRequest(transactionRequestId)
        if (response.status === TransactionRequestStatus.finish) {
          const txInfo = await getTxHashByTransactionRequestId(transactionRequestId)
          if (txInfo && txInfo.tx_hash) {
            setTransactionResult({
              hash: txInfo.tx_hash,
              status: txInfo.status as TransactionStatus,
              chain_id: txInfo.chain_id,
            })
            togglePolling(false)
          } else {
            runPolling()
          }
        } else if (response.status === TransactionRequestStatus.finish_with_error) {
          setTransactionResult({
            hash: '',
            status: 'failed',
            chain_id: 0,
          })
          togglePolling(false)
        } else {
          runPolling()
        }
      } catch (e) {
        console.error('Error during transaction polling:', e)
        runPolling()
      }
    }, 1000)
  }

  return [transactionResult, isTransactionRequestPolling]
}

export default useTransactionRequestStatusPolling
